"use client";
import { Translate } from "@/components/Translate";
import { sizes } from "@/components/bloks/Asset";
import {
	AccordionContent,
	AccordionIndicator,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/Accordion";
import { Carousel } from "@/components/ui/Carousel";
import { ProductCardWithCampaigns } from "@/features/campaigns";
import { useTranslations } from "@/i18n/client";
import type { ProductCard as IProductCard, ProductDetails } from "@/lib/centra";
import { useRecommendationClick } from "@/lib/depict/useRecommendationClick";
import { useToken } from "@frend-digital/centra/client";
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { rpc } from "#rpc";

import styles from "./StyleWithProducts.module.css";

export type RelatedProduct = NonNullable<NonNullable<ProductDetails>["relatedProducts"]>[number];

export const StyleWithProducts = ({
	productIds,
}: {
	productIds: (string | undefined)[];
}) => {
	const t = useTranslations();
	const token = useToken();

	const { data: products } = useQuery({
		queryKey: ["depict-recommendations-style-with", productIds],
		queryFn: async () => {
			const products = await rpc.depict.recommendations({
				product_ids: productIds,
				type: "cross_sell",
				limit: 8,
				token: token!,
			});

			return products;
		},
		enabled: !!productIds.length && !!token,
	});

	const handleProductClick = useRecommendationClick(products, "style_with");

	if (!products || products.length === 0) return null;

	return (
		<AccordionItem open>
			<AccordionTrigger>
				<Translate>Style with</Translate>
				<AccordionIndicator />
			</AccordionTrigger>
			<AccordionContent>
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation handled by individual product cards */}
				<div className={styles.root} onClick={handleProductClick}>
					<Carousel
						activeScroll={products?.length > 4}
						showControls={false}
						className={styles.styleWith}
					>
						{products?.map((product) => {
							return (
								<ProductCardWithCampaigns
									sizes={sizes(75, 25)}
									key={product?.displayItemId}
									product={product!}
									displayVariants={false}
									showRatings
								/>
							);
						})}
					</Carousel>
				</div>
			</AccordionContent>
		</AccordionItem>
	);
};
